/**
 * <thumbnail-nav>
 *
 * Wraps a product media gallery's thumbnail strip and adds previous/next arrows
 * that page the strip horizontally. Arrows only appear when the strip actually
 * overflows its container (i.e. there are more thumbnails than fit on screen).
 *
 * The element is self-contained: it injects its own buttons, so it survives the
 * media gallery re-rendering itself on variant changes (the new markup re-runs
 * connectedCallback).
 */
class ThumbnailNav extends HTMLElement {
  #controller = new AbortController();
  #resizeObserver;
  #container;
  #previous;
  #next;

  connectedCallback() {
    this.#container = this.querySelector('.slideshow-controls__thumbnails-container');
    if (!this.#container) return;

    const { signal } = this.#controller;

    this.#previous = this.#buildButton('previous');
    this.#next = this.#buildButton('next');
    // Buttons sit before/after the scrolling strip so they can be positioned at its edges.
    this.#container.before(this.#previous);
    this.#container.after(this.#next);

    this.#previous.addEventListener('click', () => this.#page(-1), { signal });
    this.#next.addEventListener('click', () => this.#page(1), { signal });
    this.#container.addEventListener('scroll', this.#update, { signal, passive: true });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#resize();
        this.#update();
      });
      this.#resizeObserver.observe(this.#container);
    }

    this.#resize();
    this.#update();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * @param {'previous' | 'next'} action
   * @returns {HTMLButtonElement}
   */
  #buildButton(action) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `thumbnail-nav__button thumbnail-nav__button--${action}`;
    button.setAttribute('aria-label', action === 'previous' ? 'Previous thumbnails' : 'Next thumbnails');
    button.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    return button;
  }

  /**
   * Scrolls the strip by roughly one page in the given direction.
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #page(direction) {
    if (!this.#container) return;
    // Leave a little context by paging ~80% of the visible width.
    const amount = this.#container.clientWidth * 0.8;
    this.#container.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  /** Number of thumbnails to show at once. */
  static VISIBLE = 5;

  /**
   * Sizes each thumbnail so exactly VISIBLE of them fill the container width,
   * accounting for the gap between them. Exposed as a CSS variable.
   */
  #resize() {
    const container = this.#container;
    if (!container) return;

    const strip = container.querySelector('.slideshow-controls__thumbnails');
    const gap = strip ? parseFloat(getComputedStyle(strip).columnGap) || 0 : 0;
    const visible = ThumbnailNav.VISIBLE;
    const size = (container.clientWidth - gap * (visible - 1)) / visible;
    if (size <= 0) return;

    // Trim sub-pixel excess so the initial VISIBLE thumbnails never overflow.
    this.style.setProperty('--thumbnail-size', `${Math.floor(size * 100) / 100}px`);
  }

  #update = () => {
    const container = this.#container;
    if (!container || !this.#previous || !this.#next) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const overflowing = maxScroll > 1;

    this.toggleAttribute('overflowing', overflowing);

    const scrollLeft = Math.abs(container.scrollLeft);
    this.#previous.disabled = scrollLeft <= 1;
    this.#next.disabled = scrollLeft >= maxScroll - 1;
  };
}

if (!customElements.get('thumbnail-nav')) {
  customElements.define('thumbnail-nav', ThumbnailNav);
}
